import type { DateCalendarPublicProps } from '@/components/Calendar'
import { selectUserConfiguration } from '@/features/domain/user'
import { dateUtils } from '@/utils'
import { addYears, eachDayOfInterval, endOfMonth, startOfMonth, subDays } from 'date-fns/esm'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useRecurrenceDispatch } from '../../../../../providers/StateProvider'

export function useCalendarProps(date: uui.domain.YYYYMMDD) {
  const dispatch = useRecurrenceDispatch()
  const { today } = useSelector(selectUserConfiguration)

  return useMemo<DateCalendarPublicProps>(() => {
    const selection = dateUtils.parseDate(date)
    const todayDate = dateUtils.parseDate(today)
    const minDate = startOfMonth(todayDate)
    const maxPlanningDate = addYears(minDate, 10)
    const maxDate = endOfMonth(maxPlanningDate)

    // disabled days are those before today and after the planning range
    const intervalFromStartOfMonthAndToday = {
      start: minDate,
      end: subDays(todayDate, 1),
    }

    const disabledDatesBeforeToday = dateUtils.isValidInterval(intervalFromStartOfMonthAndToday)
      ? eachDayOfInterval(intervalFromStartOfMonthAndToday)
      : []

    const disabledDatesAfterPlanningRange = eachDayOfInterval({
      start: maxPlanningDate,
      end: maxDate,
    })

    const disabledDays = [...disabledDatesBeforeToday, ...disabledDatesAfterPlanningRange].reduce<
      Record<string, number>
    >((acc, d) => {
      acc[dateUtils.convertToYYYYMMDD(d)] = 1
      return acc
    }, {})

    return {
      layout: 'date' as const,
      months: 1,
      selection,
      today,
      showNeighboringMonth: false,
      disabledDays,
      highlightToday: false,
      minDate: dateUtils.convertToYYYYMMDD(minDate),
      maxDate: dateUtils.convertToYYYYMMDD(maxDate),
      initialVisibleDate: date,

      onChange(newSelection) {
        dispatch({
          type: 'recurrenceEnd:change',
          recurrenceEnd: {
            endType: 'ON_DATE',
            endDate: dateUtils.convertToYYYYMMDD(newSelection),
          },
        })
      },
    }
  }, [today, dispatch, date])
}
